import { type Inspection } from './types';

/**
 * How an inspection's points roll up to a status. Shared so the overview chart and the
 * inspection list cannot drift apart: both read a point's notice the same way, and the
 * roll-up is the same rule the backend applies in `getInspectionStatus`.
 */

export type NoticeCounts = Record<Inspection.NoticeType, number>;

/** Worst first — the order the roll-up resolves in and the UI lists in. */
export const noticeTypesBySeverity: Inspection.NoticeType[] = ['Error', 'Warning', 'NotChecked', 'Ok'];

/**
 * A point's status is the notice on the step it currently sits at. Undefined when that
 * step is missing: that is malformed data rather than an unchecked point, so callers leave
 * it out of the counts instead of reporting something the file does not say.
 */
export const poiNoticeType = (poi: Inspection.PointOfInterest): Inspection.NoticeType | undefined => {
  const currentStep = poi.steps?.[poi.stepIndex];

  return currentStep ? currentStep.noticeType || 'NotChecked' : undefined;
};

export const countPoiNotices = (pois: Inspection.PointOfInterest[] = []): NoticeCounts => {
  const counts: NoticeCounts = { Error: 0, Warning: 0, NotChecked: 0, Ok: 0 };

  pois.forEach((poi) => {
    const noticeType = poiNoticeType(poi);

    if (noticeType) counts[noticeType]++;
  });

  return counts;
};

/**
 * The single bucket an inspection falls into: the worst notice among its points. One with
 * nothing to report on — no points, or none carrying a usable step — reads as unchecked
 * rather than as fine.
 */
export const inspectionNoticeType = (pois?: Inspection.PointOfInterest[]): Inspection.NoticeType => {
  const counts = countPoiNotices(pois);

  return noticeTypesBySeverity.find((noticeType) => counts[noticeType] > 0) ?? 'NotChecked';
};
